import HarvestFarmPicker from "./HarvestFarmPicker";
import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import api from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import {
  COOLDOWN_MS,
  newRequestId,
  tokenId,
  pendingScans,
  persistScan,
  removeScan,
  messageText,
} from "../../utils/harvestScanQueue";

function ScanWarning({ message, onResume }) {
  const dialog = useRef();
  useEffect(() => {
    const element = dialog.current;
    element.showModal();
    return () => element.close();
  }, []);
  return (
    <dialog ref={dialog} className="qr-warning-modal" role="alertdialog" aria-modal="true" aria-labelledby="qr-warning-title" aria-describedby="qr-warning-description" onCancel={(e) => e.preventDefault()}>
      <div className="qr-warning-icon" aria-hidden="true">!</div>
      <p className="qr-eyebrow">ACTION NEEDED</p>
      <h2 id="qr-warning-title">Scanning paused</h2>
      <p id="qr-warning-description">{message}</p>
      <button type="button" autoFocus onClick={onResume}>Acknowledge & resume</button>
    </dialog>
  );
}

export default function HarvestScanner({ catalog, onLocked }) {
  const { user } = useAuth();
  const [ghse, setGhse] = useState(""),
    [variety, setVariety] = useState(""),
    [verticalSection, setVerticalSection] = useState(null),
    [text, setText] = useState("");
  const [queued, setQueued] = useState(0),
    [saved, setSaved] = useState(0),
    [recent, setRecent] = useState([]);
  const [warning, setWarning] = useState(""),
    [network, setNetwork] = useState(""),
    [syncing, setSyncing] = useState(false);
  const [camera, setCamera] = useState(false),
    [starting, setStarting] = useState(false);
  const [levelPromptOpen, setLevelPromptOpen] = useState(false);
  const input = useRef(),
    reader = useRef(),
    alive = useRef(true),
    working = useRef(false),
    paused = useRef(false),
    timer = useRef(),
    idleLevelTimer = useRef(),
    idlePromptRef = useRef(),
    attempt = useRef(0);
  const selection = useRef({ ghse, variety, verticalSection }),
    lastVisible = useRef({ token: "", seen: 0 }),
    accepted = useRef(new Map()),
    confirmed = useRef(new Set());
  const syncRef = useRef(),
    enqueueRef = useRef();
  selection.current = { ghse, variety, verticalSection };
  const armIdleLevelTimer = () => {
    clearTimeout(idleLevelTimer.current);
    const selected = selection.current;
    const house = catalog.ghses.find(item => Number(item.id) === Number(selected.ghse));
    if (!selected.variety || !selected.verticalSection || !house?.requires_upper_lower || paused.current) return;
    idleLevelTimer.current = setTimeout(() => idlePromptRef.current?.(), 30000);
  };
  const promptVerticalSection = async (house, selected = null, idle = false) => {
    const result = await Swal.fire({
      title: idle ? "Confirm your harvest level" : "Where are you harvesting?",
      text: idle
        ? `No scan was taken for 30 seconds. Choose Upper or Lower again to continue in ${house.name}.`
        : `${house.name} has two capture levels. Choose the level for this scan session.`,
      input: "radio",
      inputOptions: { upper: "Upper level", lower: "Lower level" },
      inputValue: selected,
      inputValidator: value => value ? undefined : "Select Upper or Lower to continue.",
      confirmButtonText: "Continue with this level",
      confirmButtonColor: "#a92f6f",
      customClass: { popup: "harvest-level-popup" },
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    return result.isConfirmed ? result.value : null;
  };
  const isVarietyAvailableAtLevel = (varietyId, section, ghseId = ghse) => {
    const house = catalog.ghses.find(item => Number(item.id) === Number(ghseId));
    return catalog.locations.some(location =>
      Number(location.ghse_id) === Number(ghseId) &&
      Number(location.variety_id) === Number(varietyId) &&
      (!house?.requires_upper_lower || !location.vertical_section || location.vertical_section === "both" || location.vertical_section === section)
    );
  };
  idlePromptRef.current = async () => {
    if (!alive.current || paused.current) return;
    const selected = selection.current;
    const house = catalog.ghses.find(item => Number(item.id) === Number(selected.ghse));
    if (!selected.variety || !selected.verticalSection || !house?.requires_upper_lower) return;
    paused.current = true;
    setLevelPromptOpen(true);
    const section = await promptVerticalSection(house, null, true);
    if (!alive.current) return;
    if (!section) {
      setLevelPromptOpen(false);
      pause("Choose Upper or Lower to resume scanning this GHSE.");
      return;
    }
    const varietyStillAvailable = !selected.variety || isVarietyAvailableAtLevel(selected.variety, section, selected.ghse);
    const nextSelection = {
      ...selection.current,
      verticalSection: section,
      variety: varietyStillAvailable ? selected.variety : "",
    };
    selection.current = nextSelection;
    setVerticalSection(section);
    if (!varietyStillAvailable) setVariety("");
    paused.current = false;
    setLevelPromptOpen(false);
    armIdleLevelTimer();
  };
  const changeGHSE = async value => {
    clearTimeout(idleLevelTimer.current);
    if (!value) { clearTimeout(idleLevelTimer.current); setGhse(""); setVariety(""); setVerticalSection(null); return; }
    const house = catalog.ghses.find(item => String(item.id) === String(value));
    const section = house?.requires_upper_lower ? await promptVerticalSection(house) : null;
    if (house?.requires_upper_lower && !section) return;
    setGhse(value); setVariety(""); setVerticalSection(section);
  };
  const changeVerticalSection = async () => {
    const house = catalog.ghses.find(item => Number(item.id) === Number(ghse));
    if (!house?.requires_upper_lower || queued > 0 || camera || starting) return;
    clearTimeout(idleLevelTimer.current);
    const section = await promptVerticalSection(house, verticalSection);
    if (section) {
      const varietyStillAvailable = !variety || isVarietyAvailableAtLevel(variety, section);
      setVerticalSection(section);
      if (!varietyStillAvailable) setVariety("");
      selection.current = { ...selection.current, verticalSection: section, variety: varietyStillAvailable ? variety : "" };
      armIdleLevelTimer();
    }
  };
  const readQueue = () => pendingScans(user.id);
  const pause = (message) => {
    clearTimeout(idleLevelTimer.current);
    paused.current = true;
    setWarning(message);
  };
  const schedule = (delay = 80) => {
    clearTimeout(timer.current);
    if (alive.current)
      timer.current = setTimeout(() => syncRef.current(), delay);
  };
  const sync = async () => {
    if (!alive.current || paused.current || working.current) return;
    let batch;
    try {
      batch = readQueue().slice(0, 20);
      setQueued(readQueue().length);
    } catch {
      pause(
        "Browser storage is unavailable. Enable storage before continuing.",
      );
      return;
    }
    if (!batch.length) {
      setNetwork("");
      return;
    }
    working.current = true;
    setSyncing(true);
    let delay = 60;
    try {
      const { data } = await api.post(
        "field-harvest/qr/scan-batch/",
        { scans: batch.map(({ queued_at, ...payload }) => payload) },
        { timeout: 20000 },
      );
      if (!Array.isArray(data.results) || !data.results.length)
        throw new Error("Incomplete server response");
      const sent = new Map(batch.map((item) => [item.request_id, item]));
      for (const result of data.results) {
        const item = sent.get(result.request_id);
        if (!item) throw new Error("Unexpected scan receipt");
        if (result.status < 400) {
          removeScan(user.id, item.request_id);
          accepted.current.set(tokenId(item.token), Date.now());
          if (!confirmed.current.has(result.data.id)) {
            confirmed.current.add(result.data.id);
            if (alive.current) {
              setSaved((n) => n + 1);
              setRecent((items) => [result.data, ...items].slice(0, 8));
            }
          }
        } else if (
          [408, 409, 429].includes(result.status) ||
          result.status >= 500
        ) {
          throw new Error(messageText(result.data));
        } else {
          // Only definitive rejections leave the durable queue. Later items stay queued.
          removeScan(user.id, item.request_id);
          if (result.data?.code === "qr_cooldown")
            accepted.current.set(
              tokenId(item.token),
              Date.now() -
                (300 - Number(result.data.retry_after || 300)) * 1000,
            );
          else accepted.current.delete(tokenId(item.token));
          if (alive.current) pause(messageText(result.data));
          break;
        }
      }
      attempt.current = 0;
      if (alive.current) setNetwork("");
    } catch (e) {
      if (alive.current) {
        if ([400, 401, 403, 404].includes(e.response?.status))
          pause(
            messageText(e.response?.data) +
              " Your pending scans are retained; resolve the issue before resuming.",
          );
        else
          setNetwork(
            "Connection slow or unavailable. Scans are stored on this device and will retry automatically.",
          );
      }
      delay = Math.min(30000, 1000 * 2 ** Math.min(attempt.current++, 5));
    } finally {
      working.current = false;
      if (alive.current) {
        setSyncing(false);
        try {
          setQueued(readQueue().length);
        } catch {}
        schedule(delay);
      }
    }
  };
  syncRef.current = sync;
  useEffect(() => {
    alive.current = true;
    schedule(0);
    const online = () => schedule(0);
    const unload = (e) => {
      try {
        if (pendingScans(user.id).length) {
          e.preventDefault();
          e.returnValue = "";
        }
      } catch {}
    };
    window.addEventListener("online", online);
    window.addEventListener("beforeunload", unload);
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
      clearTimeout(idleLevelTimer.current);
      window.removeEventListener("online", online);
      window.removeEventListener("beforeunload", unload);
      if (reader.current?.isScanning) reader.current.stop().catch(() => {});
    };
    // The durable queue belongs to the authenticated user; latest callbacks use refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);
  useEffect(() => {
    onLocked(camera || starting || queued > 0);
    return () => onLocked(false);
  }, [camera, starting, queued, onLocked]);
  useEffect(() => {
    if (variety && !camera && !warning) input.current?.focus();
  }, [variety, camera, warning]);
  const enqueue = (raw) => {
    if (paused.current) return;
    const selected = selection.current,
      token = raw.trim(),
      id = tokenId(token);
    if (!selected.ghse || !selected.variety) {
      pause("Select the greenhouse and variety before scanning.");
      return;
    }
    if (
      !/^(SH[12]:)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        token,
      )
    ) {
      pause("This is not a valid SmartHarvest QR label.");
      return;
    }
    try {
      const queue = readQueue();
      if (queue.some((item) => tokenId(item.token) === id)) {
        pause(
          "This label is already queued. Wait for its confirmation; scanning it again will not add another net.",
        );
        return;
      }
      const elapsed = Date.now() - (accepted.current.get(id) || 0);
      if (elapsed < COOLDOWN_MS) {
        const seconds = Math.ceil((COOLDOWN_MS - elapsed) / 1000);
        pause(
          `This label was already scanned. Wait ${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s before rescanning. No additional net was queued.`,
        );
        return;
      }
      if (queue.length >= 300) {
        pause(
          "300 scans are waiting to sync. Restore the connection and sync them before collecting more nets.",
        );
        return;
      }
      const payload = {
        request_id: newRequestId(),
        token,
        ghse: Number(selected.ghse),
        variety: Number(selected.variety),
        vertical_section: selected.verticalSection,
        queued_at: Date.now(),
      };
      persistScan(user.id, payload);
      armIdleLevelTimer();
      accepted.current.set(id, Date.now());
      setQueued(queue.length + 1);
      setText("");
      if (navigator.vibrate) navigator.vibrate(20);
      if (!working.current) schedule(0);
    } catch {
      pause(
        "Browser storage is full or unavailable. This scan was NOT queued. Free storage before trying again.",
      );
    }
  };
  enqueueRef.current = enqueue;
  const startCamera = async () => {
    setStarting(true);
    try {
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import(
        "html5-qrcode"
      );
      const scanner = new Html5Qrcode("harvest-camera", {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      reader.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 15 },
        (token) => {
          if (paused.current) return;
          const now = Date.now(),
            previous = lastVisible.current;
          lastVisible.current = { token, seen: now };
          if (previous.token === token && now - previous.seen < 600) return;
          enqueueRef.current(token);
        },
        () => {},
      );
      if (!alive.current) {
        await scanner.stop();
        return;
      }
      setCamera(true);
    } catch {
      pause(
        "Camera unavailable. Allow camera permission on HTTPS or localhost, or use a USB/Bluetooth scanner.",
      );
    } finally {
      if (alive.current) setStarting(false);
    }
  };
  const stopCamera = async () => {
    try {
      await reader.current?.stop();
      setCamera(false);
    } catch {
      pause(
        "Unable to stop the camera. Reload after queued scans finish syncing.",
      );
    }
  };
  const resume = () => {
    paused.current = false;
    setWarning("");
    setText("");
    armIdleLevelTimer();
    schedule(0);
    input.current?.focus();
  };
  const varieties = catalog.varieties.filter((v) =>
    isVarietyAvailableAtLevel(v.id, verticalSection),
  );
  return (
    <section className="qr-card qr-scanner">
      <div className="qr-heading">
        <div>
          <p className="qr-eyebrow">CONTINUOUS CAPTURE</p>
          <h2>One label. One net.</h2>
          <p>Scan the next label immediately. Every physical copy is unique.</p>
        </div>

      </div>
      <div className="qr-grid">
<HarvestFarmPicker catalog={catalog} ghse={ghse} disabled={queued > 0 || camera || starting || levelPromptOpen} onChange={changeGHSE} />
        {catalog.ghses.find(item => Number(item.id) === Number(ghse))?.requires_upper_lower && <div className={`qr-level-choice is-${verticalSection}`}><span className="qr-level-icon" aria-hidden="true"><i>U</i><i>L</i></span><span className="qr-level-copy"><small>SCAN SESSION LEVEL</small><strong>{verticalSection === "upper" ? "Upper level" : "Lower level"}</strong><em>{verticalSection === "upper" ? "Top canopy · reselect after 30s idle" : "Lower canopy · reselect after 30s idle"}</em></span><span className="qr-level-active">ACTIVE</span><button type="button" disabled={queued > 0 || camera || starting || levelPromptOpen} onClick={changeVerticalSection}>Change level</button></div>}
        <label>
          Variety
          <select
            value={variety}
            disabled={!ghse || queued > 0 || camera || starting || levelPromptOpen}
            onChange={(e) => { setVariety(e.target.value); selection.current = { ...selection.current, variety: e.target.value }; clearTimeout(idleLevelTimer.current); if (e.target.value) setTimeout(() => armIdleLevelTimer(), 0); }}
          >
            <option value="">Select variety</option>
            {varieties.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {warning && <ScanWarning message={warning} onResume={resume} />}
      <div className="qr-capture-dock">
        <div className="qr-counters" aria-label="Capture totals">
          <strong>{saved} saved</strong>
          <span>{queued} queued</span>
        </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) enqueue(text);
        }}
      >
        <label>
          USB / Bluetooth scanner
          <input
            ref={input}
            value={text}
            onChange={(e) => {
              const value = e.target.value;
              setText(value);
              // Scanner keystrokes arrive individually; submit on the final QR character.
              if (/^(SH[12]:)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())) {
                enqueue(value);
              }
            }}
            onPaste={(e) => {
              const value = e.clipboardData.getData("text").trim();
              if (value) {
                e.preventDefault();
                enqueue(value);
              }
            }}
            disabled={!variety || !!warning || camera || starting || levelPromptOpen}
            autoComplete="off"
            placeholder="Scan a QR label — submits instantly"
          />
        </label>
      </form>
      {recent[0] && (
        <div className="qr-scan-success" role="status" key={saved}>
          <span className="qr-success-tick" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="m5 12 4 4L19 6" /></svg></span>
          <div><strong>Net saved</strong><small>#{recent[0].number} · {recent[0].value_adder}{recent[0].vertical_section ? ` · ${recent[0].vertical_section} level` : ""} · {recent[0].stems} stems</small></div>
        </div>
      )}
      <div className="qr-sync" role="status" aria-live="polite">
        {warning ? "Scanning paused. Acknowledge the warning to continue." : network ||
          (syncing
            ? "Saving queued nets… You can keep scanning."
            : queued
              ? "Scans securely queued on this device."
              : "Ready. Each label can be rescanned after five minutes.")}
      </div>
      </div>
      <div id="harvest-camera" />
      <button
        type="button"
        disabled={starting || levelPromptOpen || (!camera && (!variety || !!warning))}
        onClick={camera ? stopCamera : startCamera}
      >
        {camera ? "Stop camera" : "Start camera scanner"}
      </button>
      <p className="qr-help">
        The QR number links to the assigned value adder. Keep this page open
        until the queue reaches zero. Queued nets are not counted as saved until
        the server confirms them.
      </p>
      <ul className="qr-recent">
        {recent.map((r) => (
          <li key={r.id}>
            #{r.number} · {r.value_adder}{r.vertical_section ? ` · ${r.vertical_section} level` : ""} · 1 net
          </li>
        ))}
      </ul>
    </section>
  );
}
